import { useCohortsFilter } from 'features/Cohorts/hooks/useCohortsFilter'
import { SyntheticEvent, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { EOp, FilterModel, UserOrganizationalRoles, UserType, UserTypeTab } from 'types'
import { getRoute } from 'utils'
import { useAdminStatusFilter } from './useAdminStatusFilter'
import { useOrgRoleFilter } from './useOrgRoleFilter'

export const useUsersPageFilter = () => {
  const navigate = useNavigate()
  const { userType } = useParams()

  const selectedTab = useMemo(() => {
    if (!userType) return UserTypeTab.TRAINEE

    return userType as UserTypeTab
  }, [userType])

  const handleChangeSelectedTab = useCallback(
    (_: SyntheticEvent, newValue: UserTypeTab) => {
      const route = getRoute(routes.users, { userType: newValue })
      navigate(route)
    },
    [navigate],
  )

  const { cohortsFilterValue, CohortsFilter } = useCohortsFilter()
  const { RoleFilter, rolesFilterValue } = useOrgRoleFilter(selectedTab, 'type')
  const { StatusFilter, statusFilterValue } = useAdminStatusFilter()

  const filter: FilterModel = useMemo(() => {
    let baseFilter: FilterModel = {
      type: { type: 'text', op: EOp.overlap, value: [selectedTab] },
    }

    switch (selectedTab) {
      case UserTypeTab.ADMIN:
        if (rolesFilterValue) {
          delete baseFilter.type
          baseFilter = {
            ...baseFilter,
            ...rolesFilterValue,
          }
        } else {
          baseFilter.type = {
            type: 'text',
            op: EOp.overlap,
            value: [UserType.ANALYST, UserType.ADMIN],
          }
        }

        if (statusFilterValue) {
          baseFilter.licenseExpiredAt = statusFilterValue
        }
        break
      case UserTypeTab.TRAINEE:
      case UserTypeTab.CAREGIVER:
        if (cohortsFilterValue) {
          baseFilter.cohortId = cohortsFilterValue
        }
        break
      case UserTypeTab.ORG:
        if (rolesFilterValue) {
          delete baseFilter.type
          baseFilter = {
            ...baseFilter,
            ...rolesFilterValue,
          }
        } else {
          baseFilter.type = {
            type: 'text',
            op: EOp.overlap,
            value: [...UserOrganizationalRoles],
          }
        }
        break
    }
    return baseFilter
  }, [cohortsFilterValue, rolesFilterValue, selectedTab, statusFilterValue])

  return {
    filter,
    CohortsFilter,
    RoleFilter,
    StatusFilter,
    selectedTab,
    handleChangeSelectedTab,
  }
}
